/* eslint-disable no-nested-ternary */
import { TextField } from '@mui/material';
import cn from 'classnames';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import React, { forwardRef, useEffect, useState } from 'react';

import styles from './styles.module.scss';

interface InputProps {
  wrapperClassName?: string;
  inputClass?: string;
  focusClassName?: string;
  isUnderlined?: boolean;
  wrapperStyle?: React.CSSProperties;
  value: string | undefined; // Expecting E.164 formatted number "+13472548650"
  onChange: (value?: string | undefined) => void; // Returns E.164 formatted number
  errors?: { [key: string]: any };
  name: string;
  tabIndex?: number;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
}

const formatPhoneNumber = (input: string): string => {
  const cleaned = input.replace(/\D/g, '');

  if (cleaned.length === 0) return '';
  if (cleaned.length <= 3) return `(${cleaned}`;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

const PhoneInput = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      wrapperClassName,
      inputClass,
      focusClassName,
      wrapperStyle,
      isUnderlined,
      value,
      name,
      onChange,
      errors,
      tabIndex,
      disabled,
      label,
      required,
      onBlur,
    },
    ref,
  ): JSX.Element => {
    const [formattedValue, setFormattedValue] = useState<string>('');
    const nameArray = name.split('.');
    const error =
      errors && nameArray.length > 1
        ? errors[nameArray[0]]?.[nameArray[1]]?.message
        : errors
          ? errors[name]?.message
          : undefined;

    useEffect(() => {
      if (!value) {
        setFormattedValue('');
        return;
      }

      const phoneNumber = parsePhoneNumberFromString(value, 'US');
      if (phoneNumber && phoneNumber.isValid()) {
        setFormattedValue(formatPhoneNumber(phoneNumber.nationalNumber));
      }
    }, [value]);

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
      let inputValue = event.target.value.replace(/\D/g, ''); // Remove non-digits

      if (inputValue.length > 10) inputValue = inputValue.slice(0, 10); // Limit to 10 digits
      const formatted = formatPhoneNumber(inputValue);
      setFormattedValue(formatted); // Keep UI updated

      const e164PhoneNumber = inputValue.length > 0 ? `+1${inputValue}` : ''; // Store in E.164
      onChange(e164PhoneNumber);
    };

    return (
      <div className={cn(styles.inputWrapper, wrapperClassName)} style={wrapperStyle}>
        <TextField
          InputLabelProps={{
            className: 'fs-unmask',
            required: Boolean(required),
            shrink: Boolean(formattedValue),
            sx: {
              fontSize: '14px',
              backgroundColor: 'white',
              px: 0.3,
              '&.MuiInputLabel-shrink': {
                transform: 'translate(6px, -10px) scale(0.75)',
              },
              '& .MuiFormLabel-asterisk': {
                color: '#d32f2f',
                opacity: 1,
                fontWeight: 700,
              },
            },
          }}
          sx={{
            '& .MuiOutlinedInput-notchedOutline legend': { maxWidth: '0.01px !important' },
            '& .MuiInputLabel-shrink + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline legend': {
              maxWidth: '0.01px !important',
            },
          }}
          variant="outlined"
          fullWidth
          label={label}
          error={!!error}
          helperText={error}
          name={name}
          value={formattedValue}
          onChange={handleOnChange}
          className={cn(styles.input, inputClass)}
          inputProps={{
            tabIndex,
            maxLength: 17,
            'aria-required': required || undefined,
            'data-testid': `phone-input-${name}`,
            className: 'fs-exclude',
          }}
          inputRef={ref}
          disabled={disabled}
          onBlur={onBlur}
        />
        {isUnderlined && <span className={`${styles.inputFocus} ${focusClassName}`} />}
      </div>
    );
  },
);

PhoneInput.defaultProps = {
  wrapperClassName: '',
  inputClass: '',
  focusClassName: '',
  isUnderlined: true,
  wrapperStyle: {},
  errors: undefined,
  tabIndex: 0,
  disabled: false,
  onBlur: undefined,
};

export default PhoneInput;
